import React, {
    type ReactNode,
    type RefObject,
    useCallback,
    useEffect,
    useMemo,
    useReducer,
    useState
} from 'react';
import * as Styled from './OxRootContainer.styled';
import { graphql, useStaticQuery } from 'gatsby';
import { capturePostHogEvent } from 'src/utils/posthog';

import { OxHygyene } from 'src/components/OxHygene';
import { OxErrorBoundary } from 'src/components/OxErrorBoundary';
import { useQueryParam, StringParam } from 'use-query-params';
import type { WindowLocation } from '@reach/router';
import type { TClinic, TConfig, TPractitioner } from 'src/services/api/api.types';
import { ERoutes } from 'src/config/enums';
import {
    bookingFlowReducer,
    EBookingFlowAction,
    initialBookingFlowState
} from '../OxBooking/OxBookingFlowReducer';
import { clinikoGetClinicList, clinikoGetDoctors } from 'src/services/api';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

import { BookingFlowContext } from 'src/context/BookingFlowContext';
import { ETimes } from 'src/config/enums';
import { LayoutContext, type TLayoutContext } from 'src/context/LayoutContext';
import { OxFooter } from 'src/components/OxFooter';
import { OxLoader } from 'src/components/OxLoader';
import { OxStickyHeader } from 'src/components/OxStickyHeader';
import { ParallaxProvider } from 'react-scroll-parallax';
import { WebsiteDataContext } from 'src/context/WebsiteDataContext';
import throttle from 'lodash/throttle';
import { getCookie, toPx } from 'src/utils';
import { useBreakpoints } from 'src/hooks';
import { useOutboundLinkTracking } from 'src/hooks/useOutboundLinkTracking';
import { AlertContext, type TAlertData } from 'src/context/AlertContext';
import { OxAlert } from 'src/components/OxAlert/OxAlert';
import { fireEvent } from 'src/helpers/TagManager';
import { trackMetaEvent } from 'src/helpers/MetaPixelTracking';
import { captureFacebookClickId } from 'src/utils/captureFacebookClickId';
import { useValidateResponse } from 'src/hooks/useValidateResponse';
import { getCountry } from 'src/services/api/geoip';
import { CONFIG_PATH, getConfig } from 'src/services/api/config';

type TProps = {
    children: ReactNode;
    location?: WindowLocation;
};

type TCustomGQLNode<P> = {
    data: P;
};

const getStepName = (step: number): string => {
    const stepNames = [
        'book_now',
        'consultation_type',
        'location_selection',
        'doctor_selection',
        'doctor_choice',
        'date_selection',
        'thank_you'
    ];
    return stepNames[step] || `step_${step}`;
};

export const OxRootContainer = ({ location, children }: TProps): JSX.Element => {
    const prefetchedData = useStaticQuery(query);
    const isDataPrefetched =
        prefetchedData?.doctors?.nodes.length &&
        prefetchedData?.locations?.nodes?.length &&
        prefetchedData?.config?.nodes?.length;

    const { validateResponse } = useValidateResponse();
    const device = useBreakpoints();
    useOutboundLinkTracking();
    const [bookingContainerRef, setBookingContainerRef] = useState<RefObject<HTMLDivElement>>();

    /*
     * This is necessary because injecting doctors/locations into the gatsby graphql datalayer requires
     * giving them a unique ID, but we use the ID throughout the site, so we have to move it to a dummy
     * property, then move it back here...
     */
    const [doctors, setDoctors] = useState<TPractitioner[]>(() =>
        (prefetchedData?.doctors?.nodes ?? []).map(
            ({ data }: TCustomGQLNode<TPractitioner>) => data
        )
    );
    const [locations, setLocations] = useState<TClinic[]>(() =>
        (prefetchedData?.locations?.nodes ?? []).map(({ data }: TCustomGQLNode<TClinic>) => data)
    );
    const [config, setConfig] = useState<TConfig[]>(() =>
        (prefetchedData?.config?.nodes ?? []).map(({ data }: TCustomGQLNode<TConfig>) => data)
    );
    const [geoIpCountry, setCountry] = useState<string | null | undefined>('GB');
    const [debugCountry] = useQueryParam('geoip_debug', StringParam);
    const [showLoader, setShowLoader] = useState(!isDataPrefetched);
    const [animateLoader, setAnimateLoader] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);
    const [alert, setAlert] = useState<TAlertData | null>(null);
    const [bookingFlowReducerState, bookingFlowReducerDispatch] = useReducer(
        bookingFlowReducer,
        initialBookingFlowState
    );

    const [modalId, setModalId] = useState<string | null>(null);

    const setMenuIsOpenCallback = useCallback(
        throttle(setMenuIsOpen, ETimes.MenuAnimationTime),
        []
    );

    const layoutContextValue: TLayoutContext = {
        menuIsOpen,
        websiteLoaderActive: showLoader,
        setMenuIsOpen: setMenuIsOpenCallback,
        modalId: modalId,
        setModalId: setModalId
    };

    const showAlert = (data: TAlertData): void => setAlert(data);
    const hideAlert = (): void => setAlert(null);

    const errorAlertContextValue = {
        alert,
        showAlert,
        hideAlert
    };

    const defaultScopeId = 0;

    const getConfigValue = useCallback(
        (path: string, scopeId = defaultScopeId) => {
            return useMemo(() => {
                const configValues = config.filter((configItem) => configItem.path === path);

                // If not set per clinic ID, get the default
                return (
                    configValues.find((item) => item.clinicId === scopeId) ??
                    configValues.find((item) => item.clinicId === defaultScopeId)
                )?.value;
            }, [path, scopeId, config]);
        },
        [config]
    );

    const websiteDataContextValue = {
        doctors,
        locations,
        geoIpCountry,
        getConfigValue
    };

    const setBookingContainerReference = (reference: RefObject<HTMLDivElement>): void => {
        setBookingContainerRef(reference);
    };

    const scrollToBookingComponentTop = (): void => {
        if (!bookingContainerRef?.current?.offsetTop) return;

        window.scrollTo({
            top: bookingContainerRef?.current?.offsetTop - toPx(65, device),
            behavior: 'smooth'
        });
    };

    const fetchWebsiteData = async (): Promise<void> => {
        const doctorsData = await clinikoGetDoctors();
        const locationsData = await clinikoGetClinicList();
        const countryData = await getCountry();
        const configData = await getConfig();

        try {
            validateResponse(doctorsData);
            setDoctors(doctorsData ?? []);
        } catch (e) {
            console.error(e);
        }

        try {
            validateResponse(locationsData);
            setLocations(locationsData ?? []);
        } catch (e) {
            console.error(e);
        }

        if (countryData && !debugCountry) {
            try {
                validateResponse(countryData);
                setCountry(countryData?.country);
            } catch (e) {
                console.error(e);
            }
        }

        try {
            validateResponse(configData);
            setConfig(configData ?? []);
        } catch (e) {
            console.error(e);
        }
    };

    const bookingFlowContextValue = {
        reset: (): void => {
            bookingFlowReducerDispatch({
                type: EBookingFlowAction.SetBookingFlowState,
                payload: initialBookingFlowState
            });
        },
        paymentDisabled:
            getConfigValue(
                CONFIG_PATH.CHECKOUT_PAYMENT_REQUIRED,
                bookingFlowReducerState.clinic?.id
            ) !== '1',
        activeStepId: activeStep,
        state: bookingFlowReducerState,
        setBookingContainerReference,
        scrollToComponentTop: scrollToBookingComponentTop,
        dispatch: bookingFlowReducerDispatch,
        goToNextStep: (): void => {
            setActiveStep((prev) => prev + 1);
            scrollToBookingComponentTop();
        },
        goToPrevStep: (): void => {
            setActiveStep((prev) => prev - 1);
            scrollToBookingComponentTop();
        },
        goToStep: (stepIndex: number): void => {
            setActiveStep(stepIndex);
            scrollToBookingComponentTop();
        }
    };

    const onWebsiteResize = (): void => {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    const contentOnlyMode =
        ['/panel/staff/dashboard/psychological-attributes'].filter(
            (path) => location && location?.pathname.indexOf(path) !== -1
        ).length >= 1;

    const minimalHeaderAndFooter =
        [
            '/panel/staff/dashboard/treatment-plans',
            '/panel/staff/dashboard/treatment-notes',
            '/panel/staff/dashboard/refreshments'
        ].filter((path) => location && location?.pathname.indexOf(path) !== -1).length >= 1;

    useEffect(() => {
        const asyncAction = async (): Promise<void> => {
            await fetchWebsiteData();
            setShowLoader(false);
        };

        !isDataPrefetched && setAnimateLoader(true);
        onWebsiteResize();
        asyncAction();

        debugCountry && setCountry(debugCountry);
        // window.addEventListener("resize", onWebsiteResize);
        // return (): void => window.removeEventListener("resize", onWebsiteResize);

        // Track page interest (first-touch only) based on landing page
        const pathname = window.location.pathname.replace(/\/+$/, '');
        if (!getCookie('pageinterest')) {
            let interest: string | null = null;
            if (pathname.endsWith('/hair-loss')) interest = 'Hair';
            else if (pathname.endsWith('/you-skin')) interest = 'Skin';

            if (interest) {
                const expiry = new Date();
                expiry.setDate(expiry.getDate() + 90);
                document.cookie =
                    'pageinterest=' +
                    interest +
                    ';expires=' +
                    expiry.toUTCString() +
                    ';path=/;samesite=strict;secure';
            }
        }

        const getParams = window.location.search;
        // Capture the landing query when it carries campaign UTMs OR an ad click id.
        // Click ids (especially Google auto-tagged gclid) can arrive with no utm_* params.
        const landingParams = new URLSearchParams(getParams);
        const hasAdClickId = ['gclid', 'fbclid', 'gbraid', 'wbraid', 'msclkid'].some(
            (key) => (landingParams.get(key) ?? '') !== ''
        );
        const allowedKeys = [
            'utm_source','utm_medium','utm_campaign','utm_content','utm_term',
            'gclid','msclkid','gbraid','wbraid'
        ];
        if (landingParams.has('utm_campaign') || hasAdClickId) {
            // Persist only an allowlisted set of attribution params, never the raw
            // query string — avoids storing arbitrary/PII params and cookie injection
            // via a literal ';' in the URL. The backend re-whitelists these too.
            const trackingParams = new URLSearchParams();
            allowedKeys.forEach((key) => {
                const value = landingParams.get(key);
                if (value) {
                    trackingParams.set(key, value);
                }
            });

            const date = new Date();
            //set to 90 days same as FB
            date.setDate(date.getDate() + 90);
            document.cookie =
                'utmtracking=' +
                trackingParams.toString() +
                ';expires=' +
                date.toUTCString() +
                ';path=/;samesite=strict' +
                (window.location.protocol === 'https:' ? ';secure' : '');

            // Track UTM landing in PostHog
            // Whitelist and sanitize UTM params; avoid sending raw FB IDs
            const utmParams = new URLSearchParams(getCookie('utmtracking') ?? '');
            const utmProps = Array.from(utmParams.entries())
                .filter(([k]) => allowedKeys.includes(k))
                .reduce((acc, [k, v]) => {
                    acc[k] = String(v).slice(0, 200);
                    return acc;
                }, {} as Record<string, string>);
            // Optional: if you have a consent flag, guard this emit here.
            capturePostHogEvent('utm_tracking_landing', {
                ...utmProps,
                has_fbp: Boolean(getCookie('_fbp')),
                has_fbc: Boolean(getCookie('_fbc'))
            });
        }
    }, []);

    useEffect(() => {
        let props = {};

        switch (activeStep) {
            default:
            case 0:
                props = {
                    ecommerce: {
                        checkout: {
                            actionField: {
                                step: 1
                            }
                        }
                    }
                };
                break;
            case 1:
                //Book Now
                props = {
                    ecommerce: {
                        checkout: {
                            actionField: {
                                step: 2
                            }
                        }
                    }
                };
                break;
            case 2:
                //Where would you like your consultation
                props = {
                    ecommerce: {
                        checkout: {
                            actionField: {
                                step: 3,
                                option: bookingFlowReducerState.userLoggedIn ? 'Logged In' : 'Guest'
                            }
                        }
                    }
                };
                break;
            case 3: {
                //Do you know which Doctor you would like to book with?
                let eventLocation = '';
                locations.forEach((element) => {
                    if (element.id == bookingFlowReducerState.consultationLocationId) {
                        eventLocation = element.displayName ?? '';
                    }
                });
                props = {
                    ecommerce: {
                        checkout: {
                            actionField: {
                                step: 4,
                                option: eventLocation
                            }
                        }
                    }
                };
                break;
            }
            case 4:
                //Now, select your Doctor to view available appointments
                props = {
                    ecommerce: {
                        checkout: {
                            actionField: {
                                step: 5
                                //'option': bookingFlowReducerState.chooseDoctorForMe ? 'Choose Doctor for me' : 'Has Favourite Doctor'
                            }
                        }
                    }
                };
                break;
            case 5:
                //Select Date
                props = {
                    ecommerce: {
                        checkout: {
                            actionField: {
                                step: 6,
                                option: bookingFlowReducerState.chooseDoctorForMe
                                    ? 'Choose Doctor for me'
                                    : 'Has Favourite Doctor'
                            }
                        }
                    }
                };
                break;
            case 6:
                //Thank You
                props = {
                    ecommerce: {
                        checkout: {
                            actionField: {
                                step: 7
                            }
                        }
                    }
                };
                break;
        }

        if (props && Object.keys(props).length !== 0 && props.constructor === Object) {
            fireEvent({ event: 'checkoutStep', props });
            
            // Only track booking step progress when on appointment/booking pages
            const pathname = (location?.pathname ?? '').replace(/\/+$/, '');
            const isOnBookingPage =
                pathname.startsWith(ERoutes.Appointment.replace(/\/+$/, '')) ||
                pathname.startsWith(ERoutes.Book.replace(/\/+$/, ''));
            
            if (isOnBookingPage) {
                // Also track in PostHog
                capturePostHogEvent('booking_step_progress', {
                    step: activeStep + 1,
                    step_name: getStepName(activeStep),
                    is_logged_in: bookingFlowReducerState.userLoggedIn,
                    consultation_location_id: bookingFlowReducerState.consultationLocationId,
                    choose_doctor_for_me: bookingFlowReducerState.chooseDoctorForMe
                });
            }
        }
    }, [activeStep, bookingFlowReducerState.currentStage]);

    useEffect(() => {
        if (menuIsOpen) disableBodyScroll(document.body, { reserveScrollBarGap: true });
        else enableBodyScroll(document.body);
    }, [menuIsOpen]);

    // Track PageView with hybrid Meta Pixel + CAPI on route changes
    // Also capture Facebook Click ID (fbclid) from URL parameters
    useEffect(() => {
        // Capture fbclid parameter and create _fbc cookie for attribution
        captureFacebookClickId();

        // Track PageView event
        trackMetaEvent({
            eventName: 'PageView'
        });
    }, [location?.pathname]);

    return (
        <OxErrorBoundary>
            <LayoutContext.Provider value={layoutContextValue}>
                <WebsiteDataContext.Provider value={websiteDataContextValue}>
                    <BookingFlowContext.Provider value={bookingFlowContextValue}>
                        <AlertContext.Provider value={errorAlertContextValue}>
                            <ParallaxProvider>
                                <OxLoader animate={animateLoader} show={showLoader} />
                                <Styled.Layout data-geo-ip={geoIpCountry}>
                                    {!contentOnlyMode && (
                                        <OxStickyHeader
                                            minimalHeaderAndFooter={minimalHeaderAndFooter}
                                        />
                                    )}
                                    {children}
                                    <OxAlert />
                                    {!contentOnlyMode && (
                                        <>
                                            <OxHygyene />
                                            <OxFooter minimalHeaderAndFooter={minimalHeaderAndFooter} />
                                        </>
                                    )}
                                </Styled.Layout>
                            </ParallaxProvider>
                        </AlertContext.Provider>
                    </BookingFlowContext.Provider>
                </WebsiteDataContext.Provider>
            </LayoutContext.Provider>
        </OxErrorBoundary>
    );
};

const query = graphql`
    query DataQuery {
        doctors: allMiddlewareDoctors {
            nodes {
                data {
                    clinicId
                    description
                    designation
                    firstName
                    id
                    label
                    lastName
                    practitionerId
                    showInOnlineBookings
                    title
                }
            }
        }

        locations: allMiddlewareLocations {
            nodes {
                data {
                    id
                    businessId
                    businessName
                    displayName
                    businessRegistrationName
                    businessRegistrationValue
                    address1
                    address2
                    city
                    state
                    postCode
                    countryCode
                    appointmentRemindersEnabled
                    showInOnlineBookings
                    contactInformation
                    label
                    emailReplyTo
                    websiteAddress
                    accountInstance {
                        clinikoUrl
                    }
                    currency
                    preTreatmentAppointmentTypeId
                    preTreatmentPractitionerId
                    active
                    virtualAppointmentTypeId
                    virtualPractitionerId
                    defaultAppointmentTypeId
                    openDays
                    timezone
                }
            }
        }

        config: allMiddlewareConfigs {
            nodes {
                data {
                    clinicId
                    path
                    scope
                    value
                }
            }
        }
    }
`;
